import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { PASSWORD_MIN } from '@bs/contracts';
import { api } from '../../api/instance.js';
import { ApiError, errorMessage } from '../../api/client.js';
import { AuthShell } from './AuthShell.js';
import { FormField } from '../../components/ui/form-field.js';
import { Input } from '../../components/ui/input.js';
import { Button } from '../../components/ui/button.js';
import { InlineAlert } from '../../components/ui/states.js';

/**
 * Read the invite's email + token. The API sends `/accept-invite#email=…&token=…` (the fragment never reaches a
 * server log); older or hand-made links use `?email=…&token=…`. Both are read; the fragment wins when both carry
 * a value. BS: this page only read the query, so every real invite link showed "Invite link is incomplete".
 * A CloudFront SPA error-page rewrite keeps the browser URL (and its fragment) as is, so this works there too.
 */
export function readInviteParams(search: string, hash: string): { email: string; token: string } {
  const q = new URLSearchParams(search.replace(/^\?/, ''));
  const h = new URLSearchParams(hash.replace(/^#/, '').replace(/^\?/, ''));
  const pick = (k: string) => (h.get(k) || q.get(k) || '').trim();
  return { email: pick('email'), token: pick('token') };
}

/** /accept-invite#email=…&token=… (or ?email=…&token=…): choose a password (10+ characters). Single-use, expires. */
export function AcceptInvitePage() {
  const loc = useLocation();
  // react-router's location plus window.location: whichever still carries the fragment after any redirect.
  const fromRouter = readInviteParams(loc.search, loc.hash);
  const fromWindow = typeof window === 'undefined' ? { email: '', token: '' } : readInviteParams(window.location.search, window.location.hash);
  const token = fromRouter.token || fromWindow.token;
  const [email, setEmail] = useState(fromRouter.email || fromWindow.email);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.call('acceptInvite', { body: { email, token, password } });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'VALIDATION_FAILED' && err.details
        ? err.details.map((d) => d.message).join('; ')
        : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold tracking-tight">Invite link is incomplete</h1>
        <p className="mt-2 text-sm text-muted-foreground">Open the full link from your invite email, or ask your admin to send a new one.</p>
        <Button asChild variant="outline" className="mt-6"><Link to="/login">Go to sign in</Link></Button>
      </AuthShell>
    );
  }
  if (done) {
    return (
      <AuthShell>
        <CheckCircle2 className="h-10 w-10 text-success" aria-hidden />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">You're all set</h1>
        <p className="mt-2 text-sm text-muted-foreground">Your password is saved and your account is active.</p>
        <Button asChild size="lg" className="mt-6 w-full"><Link to="/login">Sign in</Link></Button>
      </AuthShell>
    );
  }
  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold tracking-tight">Set your password</h1>
      <p className="mt-1 text-sm text-muted-foreground">Welcome to Business Sense. Choose a password to finish setting up.</p>
      <form className="mt-8 space-y-4" onSubmit={submit}>
        <FormField help="f.auth.email" label="Work email"><Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></FormField>
        <FormField help="f.auth.newPassword" label="New password" hint={`At least ${PASSWORD_MIN} characters.`}>
          <Input type="password" autoComplete="new-password" required minLength={PASSWORD_MIN} value={password} onChange={(e) => setPassword(e.target.value)} />
        </FormField>
        <FormField help="f.auth.again" label="Type it again" error={mismatch ? 'The two passwords do not match.' : null}>
          <Input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </FormField>
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={mismatch}>Save password</Button>
      </form>
    </AuthShell>
  );
}
